// src/components/PWAHelper.js - Versión Optimizada sin re-renders
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Wifi, WifiOff, Download, Smartphone, Bell, RefreshCw } from 'lucide-react';

const PWAHelper = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  
  // Usar refs para evitar re-renders de timers
  const swRegistration = useRef(null);
  const installTimerRef = useRef(null);
  const notificationTimerRef = useRef(null);
  const toastTimeoutRef = useRef(null);

  // Memoizar funciones para evitar re-renders
  const showToast = useCallback((message, type = 'info') => {
    // Limpiar timeout anterior si existe
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    // Crear toast notification
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      box-shadow: 0 10px 25px rgba(0,0,0,0.1);
      z-index: 10000;
      animation: slideIn 0.3s ease-out;
      max-width: 300px;
      font-size: 14px;
      font-weight: 500;
    `;
    
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    
    toast.textContent = message;
    document.body.appendChild(toast);
    
    toastTimeoutRef.current = setTimeout(() => {
      if (toast.parentNode) toast.remove();
      if (style.parentNode) style.remove();
      toastTimeoutRef.current = null;
    }, 4000);
  }, []);

  // Detectar si ya está instalado - SOLO UNA VEZ
  useEffect(() => {
    const checkIfInstalled = () => {
      const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebAppMode = window.navigator.standalone === true;
      setIsInstalled(isInStandaloneMode || isInWebAppMode);
      return isInStandaloneMode || isInWebAppMode;
    };

    const installed = checkIfInstalled();

    // Solo configurar timers si NO está instalado
    if (!installed) {
      // Timer para install prompt - SOLO UNA VEZ
      if (!localStorage.getItem('pwa-install-dismissed')) {
        installTimerRef.current = setTimeout(() => {
          setShowInstallPrompt(true);
        }, 10000);
      }

      // Timer para notification prompt - SOLO UNA VEZ
      if ('Notification' in window && Notification.permission === 'default') {
        notificationTimerRef.current = setTimeout(() => {
          setShowNotificationPrompt(true);
        }, 15000);
      }
    }

    // Cleanup
    return () => {
      if (installTimerRef.current) clearTimeout(installTimerRef.current);
      if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
    };
  }, []); // Sin dependencias - solo se ejecuta una vez

  // Listeners de eventos - MEMOIZADOS
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Solo mostrar si no está instalado y no fue rechazado antes
      if (!isInstalled && !localStorage.getItem('pwa-install-dismissed')) {
        setShowInstallPrompt(true);
      }
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      
      // Mostrar notificación de bienvenida SIN timeout problemático
      if (Notification.permission === 'granted') {
        try {
          new Notification('¡TechStore instalado!', {
            body: 'Ahora puedes acceder rápidamente desde tu pantalla de inicio',
            icon: '/icons/icon-192x192.png'
          });
        } catch (error) {
          console.log('Error showing notification:', error);
        }
      }
    };

    // Agregar listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]); // Solo re-ejecutar si cambia isInstalled

  // Service Worker registration - SOLO UNA VEZ
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          swRegistration.current = registration;
          
          // Escuchar actualizaciones del SW
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setShowUpdatePrompt(true);
                }
              });
            }
          });
        })
        .catch(error => console.log('SW registration failed:', error));

      // Escuchar mensajes del Service Worker
      const handleSWMessage = (event) => {
        if (event.data.type === 'CART_SYNCED') {
          showToast('Carrito sincronizado correctamente', 'success');
        }
        if (event.data.type === 'CACHE_UPDATED') {
          showToast('Contenido actualizado disponible offline', 'info');
        }
      };

      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      };
    }
  }, [showToast]); // Solo depende de showToast que es estable

  // Handlers memoizados
  const handleInstallClick = useCallback(async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('Usuario aceptó la instalación');
        } else {
          console.log('Usuario rechazó la instalación');
          localStorage.setItem('pwa-install-dismissed', 'true');
        }
      } catch (error) {
        console.log('Error installing app:', error);
      }
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  }, [deferredPrompt]);

  const handleUpdateClick = useCallback(() => {
    if (swRegistration.current && swRegistration.current.waiting) {
      swRegistration.current.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }, []);

  const handleNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          showToast('Notificaciones habilitadas', 'success');
          
          // Suscribirse a push notifications si está disponible
          if (swRegistration.current && 'pushManager' in swRegistration.current) {
            console.log('Push notifications setup');
          }
        }
      } catch (error) {
        console.error('Error requesting notification permission:', error);
      }
    }
    setShowNotificationPrompt(false);
  }, [showToast]);

  const shareApp = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'TechStore - Tu Tienda de Tecnología',
          text: 'Descubre los mejores productos tecnológicos',
          url: window.location.origin
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback para navegadores sin Web Share API
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(window.location.origin);
          showToast('Link copiado al portapapeles', 'success');
        } catch (error) {
          console.log('Error copying to clipboard:', error);
        }
      }
    }
  }, [showToast]);

  const enableOfflineMode = useCallback(() => {
    // Cachear productos populares para modo offline
    if ('serviceWorker' in navigator && swRegistration.current) {
      swRegistration.current.active?.postMessage({
        type: 'CACHE_POPULAR_PRODUCTS'
      });
      showToast('Productos guardados para uso offline', 'success');
    }
  }, [showToast]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (installTimerRef.current) clearTimeout(installTimerRef.current);
      if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  return (
    <>
      {/* Indicador de Estado de Conexión */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-orange-500 text-white text-center py-2 text-sm font-medium z-50 animate-pulse">
          <WifiOff className="inline w-4 h-4 mr-2" />
          Sin conexión - Modo offline activado
        </div>
      )}

      {/* Prompt de Instalación PWA */}
      {showInstallPrompt && !isInstalled && (
        <div className="fixed bottom-4 left-4 right-4 bg-white rounded-lg shadow-xl border p-4 z-50 max-w-sm mx-auto animate-slide-up">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">¡Instala TechStore!</h3>
              <p className="text-sm text-gray-600 mb-3">
                Accede más rápido y disfruta de una experiencia nativa
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleInstallClick}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  Instalar
                </button>
                <button
                  onClick={() => {
                    setShowInstallPrompt(false);
                    localStorage.setItem('pwa-install-dismissed', 'true');
                  }}
                  className="text-gray-600 px-4 py-2 text-sm hover:text-gray-800 transition-colors"
                >
                  Ahora no
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prompt de Actualización */}
      {showUpdatePrompt && (
        <div className="fixed bottom-4 left-4 right-4 bg-blue-600 text-white rounded-lg shadow-xl p-4 z-50 max-w-sm mx-auto">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-6 h-6 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Nueva versión disponible</h3>
              <p className="text-sm opacity-90 mb-3">
                Actualiza para obtener las últimas mejoras
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleUpdateClick}
                  className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  Actualizar
                </button>
                <button
                  onClick={() => setShowUpdatePrompt(false)}
                  className="text-white/80 px-4 py-2 text-sm hover:text-white transition-colors"
                >
                  Después
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prompt de Notificaciones */}
      {showNotificationPrompt && (
        <div className="fixed bottom-4 left-4 right-4 bg-white rounded-lg shadow-xl border p-4 z-50 max-w-sm mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Bell className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">Mantente informado</h3>
              <p className="text-sm text-gray-600 mb-3">
                Recibe notificaciones sobre ofertas y nuevos productos
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleNotificationPermission}
                  className="bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-700 transition-colors"
                >
                  Permitir
                </button>
                <button
                  onClick={() => setShowNotificationPrompt(false)}
                  className="text-gray-600 px-4 py-2 text-sm hover:text-gray-800 transition-colors"
                >
                  No gracias
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botones de Acción PWA (solo visible si no está instalado) */}
      {!isInstalled && (
        <div className="fixed bottom-20 right-4 z-40">
          <div className="flex flex-col gap-2">
            {deferredPrompt && (
              <button
                onClick={handleInstallClick}
                className="w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-200 flex items-center justify-center group hover:w-auto hover:px-4"
                title="Instalar App"
              >
                <Download className="w-5 h-5" />
                <span className="ml-2 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Instalar
                </span>
              </button>
            )}
            
            <button
              onClick={shareApp}
              className="w-12 h-12 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-all duration-200 flex items-center justify-center group hover:w-auto hover:px-4"
              title="Compartir App"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              <span className="ml-2 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Compartir
              </span>
            </button>
          </div>
        </div>
      )}

      {/* CSS para animaciones */}
      <style jsx>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default PWAHelper;
